import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'

import { getUserName } from '../utils'

import { useQueryParams } from './queryParamsContext'
import { TimezoneProvider } from './timezoneContext'

import { LocaleProvider } from '@/context/localeContext'
import { usePublicProject } from '@/hooks/usePublicProject'
import { useRotatingQrCodeAccess } from '@/hooks/useRotatingQrCodeAccess'
import { useRouterParams } from '@/hooks/useRouterParams'
import { useWebInduction } from '@/hooks/useWebInduction'
import {
  ShedData,
  useWorkerAuthentication,
} from '@/hooks/useWorkerAuthentication'
import {
  GET_USER_WITH_VISITOR,
  GET_USER_WITH_WORKER,
} from '@/modules/webInduction/graphQL'

import {
  UserWithVisitorUiQuery,
  UserWithVisitorUiQueryVariables,
  UserWithWorkerUiQuery,
  UserWithWorkerUiQueryVariables,
} from 'types/api.graphql'

type Context = {
  project: ReturnType<typeof usePublicProject>
  pageLoading: boolean
  warnAboutAccess: boolean
  projectId: string | null
  setProjectId: (projectId: string | null) => void
  originalProjectId: string | null
}

const WorkerWebContext = React.createContext<Context>({
  project: { loading: true, publicProject: null, projectId: null },
  pageLoading: true,
  warnAboutAccess: false,
  projectId: null,
  setProjectId: () => {},
  originalProjectId: null,
})

type Props = {
  children: React.ReactNode
  requireUser: boolean
  mode: ShedData['workerType']
}

/**
 * The main purpose of this provider is to fetch the user at a high level when needed. This is to create single,
 * consistent place for code to be executed when the user is required. This context doesn't really provide much
 * information as it all gets set in the shed. Don't love this pattern but it is what it is.
 */
export const WorkerWebProvider: React.FC<Props> = ({
  children,
  requireUser,
  mode,
}) => {
  const { push, events, asPath } = useRouter()

  const { projectId: routeProjectId, isReady } = useRouterParams<{
    projectId: string
  }>()
  const { params } = useQueryParams<{
    projectId: string
    successUrl: string
  }>({
    clearOnRouteChange: ['projectId'],
  })

  const { hasAccess } = useRotatingQrCodeAccess() // will automatically grant access if the code is available

  const { goToWorkerAuthentication, workerAuthenticationData } =
    useWorkerAuthentication()

  const originalProjectId = useRef<string | null>(null)
  const [projectId, setProjectId] = useState<string | null>(null)

  useEffect(() => {
    if (isReady && !originalProjectId.current) {
      const extractProjectId = (url: string) => {
        if (!url) return null
        try {
          const urlParams = new URLSearchParams(
            url.includes('?') ? url.split('?')[1] : ''
          )
          return urlParams?.get('projectId')
        } catch (error) {
          console.error('Failed to parse URL:', error)
          return null
        }
      }

      const newProjectId =
        routeProjectId ||
        params?.projectId ||
        workerAuthenticationData?.projectId ||
        extractProjectId(params?.successUrl ?? asPath) ||
        null

      originalProjectId.current = newProjectId
      setProjectId(newProjectId)
    }
  }, [
    isReady,
    routeProjectId,
    params?.projectId,
    params?.successUrl,
    workerAuthenticationData?.projectId,
    asPath,
  ])

  const {
    goTo,
    isLoading,
    userWithWorker,
    userWithVisitor,
    ingestUserWithWorker,
    ingestUserWithVisitor,
  } = useWebInduction()

  const hasUser = !!(userWithWorker || userWithVisitor)

  const [dataLoading, setDataLoading] = useState(requireUser && !hasUser) // if we already have a user, don't fetch it
  const [warnAboutAccess, setWarnAboutAccess] = useState(false)

  const validateAccess = useCallback(
    (project: Context['project']['publicProject']) => {
      if (!project?.qrCodeRotationEnabled) return

      // if we don't require a user, we can show the warning, but as soon as we are on an authenticated page, we MUST redirect
      if (!requireUser) {
        setWarnAboutAccess(!hasAccess)
        return
      }

      if (!hasAccess) {
        push(goTo.urls.enterProjectCode)
      }
    },
    [hasAccess, goTo.urls.enterProjectCode, push, requireUser]
  )

  const project = usePublicProject(projectId, validateAccess)

  const bailOut = (workerType: Props['mode']) => {
    goToWorkerAuthentication.authenticate({
      workerType,
      successUrl: goTo.urls.landing(project?.projectId),
      failureUrl: goTo.urls.appOrWeb,
    })
  }

  const fetchWorker = useMemo(() => {
    if (!isReady) return false
    if (mode !== 'WORKER') return false
    if (!requireUser) return false
    if (isLoading) return false
    if (userWithWorker) return false

    return true
  }, [isReady, isLoading, mode, requireUser, userWithWorker])

  const { data: workerData } = useQuery<
    UserWithWorkerUiQuery,
    UserWithWorkerUiQueryVariables
  >(GET_USER_WITH_WORKER, {
    skip: !fetchWorker,
    onCompleted: (data) => {
      if (data?.user?.id) {
        ingestUserWithWorker(data.user)
        setDataLoading(false)
      } else {
        bailOut('WORKER')
      }
    },
    onError: () => {
      bailOut('WORKER')
    },
  })

  const fetchVisitor = useMemo(() => {
    if (!isReady) return false
    if (mode !== 'VISITOR') return false
    if (!requireUser) return false
    if (isLoading) return false
    if (userWithVisitor) return false

    return true
  }, [isReady, isLoading, mode, requireUser, userWithVisitor])

  const { data: visitorData } = useQuery<
    UserWithVisitorUiQuery,
    UserWithVisitorUiQueryVariables
  >(GET_USER_WITH_VISITOR, {
    skip: !fetchVisitor,
    onCompleted: (data) => {
      if (data?.user?.id) {
        ingestUserWithVisitor(data.user)
        setDataLoading(false)
      } else {
        bailOut('VISITOR')
      }
    },
    onError: () => {
      bailOut('VISITOR')
    },
  })

  useEffect(() => {
    const onRouteChange = () => validateAccess(project.publicProject)
    events.on('routeChangeStart', onRouteChange)

    return () => {
      events.off('routeChangeStart', onRouteChange)
    }
  }, [events, project.publicProject, validateAccess])

  return (
    <WorkerWebContext.Provider
      value={{
        project,
        pageLoading: dataLoading,
        warnAboutAccess,
        projectId,
        setProjectId,
        originalProjectId: originalProjectId.current,
      }}
    >
      <LocaleProvider
        localeCountryCode={
          project.publicProject?.locale ??
          project.publicProject?.builder?.defaultLocale
        }
        buildPassOptions={{
          builderName: project.publicProject?.builder?.company?.name,
          projectName: project.publicProject?.name,
          userName: getUserName(workerData?.user ?? visitorData?.user),
        }}
      >
        <TimezoneProvider timezoneOverride={project.publicProject?.timezone}>
          {children}
        </TimezoneProvider>
      </LocaleProvider>
    </WorkerWebContext.Provider>
  )
}

export const useWorkerWebContext = () => useContext(WorkerWebContext)
